/** XeAgents.ai**/
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { CalendarDays, ExternalLink, CheckCircle2, XCircle, Loader2, AlertCircle } from "lucide-react";

interface CalComSettings {
  configured: boolean;
  username: string;
  eventSlug: string;
  eventTypeId: string;
  hasApiKey: boolean;
  guestEmails: string;
}

type CalendarProvider = 'calcom' | 'microsoft' | 'google';

interface CalendarProviderSettings {
  provider: CalendarProvider;
  microsoftConfigured: boolean;
  googleConfigured: boolean;
  googleClientId?: string;
}

interface GoogleCalendarStatus {
  connected: boolean;
  email?: string;
  calendarId?: string;
  isShared?: boolean;
  message?: string;
  lastUsedAt?: string;
  lastError?: string;
}

interface CalComEventType {
  id: number;
  title: string;
  slug: string;
  duration: number;
}

export default function CalendarIntegrationSettings() {
  const { toast } = useToast();

  // Calendar provider selection
  const [calendarProvider, setCalendarProvider] = useState<CalendarProvider>('calcom');

  // Cal.com settings state
  const [calcomApiKey, setCalcomApiKey] = useState("");
  const [calcomUsername, setCalcomUsername] = useState("");
  const [calcomEventSlug, setCalcomEventSlug] = useState("");
  const [calcomEventTypeId, setCalcomEventTypeId] = useState("");
  const [calcomGuestEmails, setCalcomGuestEmails] = useState("");
  const [calcomTestResult, setCalcomTestResult] = useState<{ success: boolean; message: string; eventTypes?: CalComEventType[] } | null>(null);

  // Microsoft Outlook settings state
  const [microsoftTestResult, setMicrosoftTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Google Calendar settings state
  const [googleTestResult, setGoogleTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: calcomSettings } = useQuery<CalComSettings>({
    queryKey: ["/api/flow-automation/calcom-settings"],
  });

  const { data: calendarProviderSettings } = useQuery<CalendarProviderSettings>({
    queryKey: ["/api/flow-automation/calendar-provider-settings"],
  });

  const { data: googleCalendarStatus, refetch: refetchGoogleStatus } = useQuery<GoogleCalendarStatus>({
    queryKey: ["/api/flow-automation/google-calendar/status"],
    enabled: calendarProvider === 'google',
  });

  useEffect(() => {
    if (calcomSettings) {
      setCalcomUsername(calcomSettings.username || "");
      setCalcomEventSlug(calcomSettings.eventSlug || "");
      setCalcomEventTypeId(calcomSettings.eventTypeId || "");
      setCalcomGuestEmails(calcomSettings.guestEmails || "");
    }
  }, [calcomSettings]);

  useEffect(() => {
    if (calendarProviderSettings) {
      setCalendarProvider(calendarProviderSettings.provider || 'calcom');
    }
  }, [calendarProviderSettings]);

  // Handle Google OAuth callback URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleConnected = urlParams.get('google_connected');
    const googleError = urlParams.get('google_error');

    if (googleConnected === 'true') {
      toast({
        title: "Google Calendar Connected",
        description: "Your Google Calendar has been connected successfully!",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/google-calendar/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      window.history.replaceState({}, '', window.location.pathname);
      setCalendarProvider('google');
    } else if (googleError) {
      toast({
        title: "Google Calendar Connection Failed",
        description: decodeURIComponent(googleError),
        variant: "destructive",
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  const saveCalcomSettingsMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        apiKey: calcomApiKey || undefined,
        username: calcomUsername || undefined,
        eventSlug: calcomEventSlug || undefined,
        eventTypeId: calcomEventTypeId || undefined,
        guestEmails: calcomGuestEmails || undefined,
      };
      const res = await apiRequest("PUT", "/api/flow-automation/calcom-settings", payload);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calcom-settings"] });
      toast({
        title: "Cal.com Settings Saved",
        description: data.configured ? "Your Cal.com integration is now active." : "Settings saved. Please add your API key to activate.",
      });
      setCalcomApiKey("");
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Save Cal.com Settings",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const testCalcomConnectionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/flow-automation/calcom-settings/test", {});
      return res.json();
    },
    onSuccess: (data) => {
      setCalcomTestResult(data);
      if (data.success) {
        toast({ title: "Connection Successful", description: data.message });
      } else {
        toast({ title: "Connection Failed", description: data.error, variant: "destructive" });
      }
    },
    onError: (error: any) => {
      setCalcomTestResult({ success: false, message: error.message });
      toast({ title: "Connection Test Failed", description: error.message, variant: "destructive" });
    },
  });

  const saveCalendarProviderMutation = useMutation({
    mutationFn: async (provider: CalendarProvider) => {
      const res = await apiRequest("PUT", "/api/flow-automation/calendar-provider-settings", { provider });
      return res.json();
    },
    onSuccess: (_data, provider) => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      toast({
        title: "Calendar Provider Updated",
        description: `Switched to ${provider === 'calcom' ? 'Cal.com' : provider === 'microsoft' ? 'Microsoft Outlook' : 'Google Calendar'} for availability checking.`,
      });
    },
    onError: (error: any) => {
      toast({ title: "Failed to Update Calendar Provider", description: error.message, variant: "destructive" });
    },
  });

  const testMicrosoftConnectionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/flow-automation/microsoft-calendar/test", {});
      return res.json();
    },
    onSuccess: (data) => {
      setMicrosoftTestResult(data);
      if (data.success) {
        toast({ title: "Microsoft Connection Successful", description: data.message });
      } else {
        toast({ title: "Microsoft Connection Failed", description: data.error, variant: "destructive" });
      }
    },
    onError: (error: any) => {
      setMicrosoftTestResult({ success: false, message: error.message });
      toast({ title: "Microsoft Connection Test Failed", description: error.message, variant: "destructive" });
    },
  });

  const testGoogleConnectionMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/flow-automation/google-calendar/test", {});
      return res.json();
    },
    onSuccess: (data) => {
      setGoogleTestResult(data);
      if (data.success) {
        toast({ title: "Google Calendar Connection Successful", description: data.message });
      } else {
        toast({ title: "Google Calendar Connection Failed", description: data.error, variant: "destructive" });
      }
    },
    onError: (error: any) => {
      setGoogleTestResult({ success: false, message: error.message });
      toast({ title: "Google Connection Test Failed", description: error.message, variant: "destructive" });
    },
  });

  const connectGoogleCalendarMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("GET", "/api/flow-automation/google-calendar/authorize", {});
      return res.json();
    },
    onSuccess: (data) => {
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        toast({ title: "Connection Failed", description: data.error || "Failed to get authorization URL", variant: "destructive" });
      }
    },
    onError: (error: any) => {
      toast({ title: "Connection Failed", description: error.message, variant: "destructive" });
    },
  });

  const disconnectGoogleCalendarMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/flow-automation/google-calendar/disconnect", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/google-calendar/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/calendar-provider-settings"] });
      toast({ title: "Disconnected", description: "Google Calendar has been disconnected successfully." });
    },
    onError: (error: any) => {
      toast({ title: "Disconnect Failed", description: error.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Calendar Integration</CardTitle>
          <CardDescription>
            Configure your calendar provider for availability checking and automatic appointment booking during voice calls.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Calendar Provider Selection */}
          <div className="space-y-3">
            <Label>Calendar Provider</Label>
            <p className="text-sm text-muted-foreground">
              Choose which calendar service to use for availability checking and meeting invites.
            </p>
            <Select
              value={calendarProvider}
              onValueChange={(value: CalendarProvider) => {
                setCalendarProvider(value);
                saveCalendarProviderMutation.mutate(value);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a calendar provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="calcom">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    <span>Cal.com</span>
                    {calcomSettings?.configured && <CheckCircle2 className="h-3 w-3 text-green-500 ml-1" />}
                  </div>
                </SelectItem>
                <SelectItem value="microsoft">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    <span>Microsoft Outlook</span>
                    {calendarProviderSettings?.microsoftConfigured && <CheckCircle2 className="h-3 w-3 text-green-500 ml-1" />}
                  </div>
                </SelectItem>
                <SelectItem value="google">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    <span>Google Calendar</span>
                    {calendarProviderSettings?.googleConfigured && <CheckCircle2 className="h-3 w-3 text-green-500 ml-1" />}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Cal.com Settings */}
          {calendarProvider === 'calcom' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-4">
                <div className="flex items-start gap-3">
                  <CalendarDays className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Cal.com Integration
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Connect your Cal.com account to enable real-time availability checking and automatic appointment booking during voice calls.
                    </p>
                    <a
                      href="https://cal.com/settings/developer/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                    >
                      Get your API key from Cal.com
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>

              {calcomSettings?.configured && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm text-green-700 dark:text-green-300">
                    Cal.com integration is active
                  </span>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="calcom-api-key">
                    API Key {calcomSettings?.hasApiKey && <span className="text-xs text-muted-foreground">(leave blank to keep existing)</span>}
                  </Label>
                  <Input
                    id="calcom-api-key"
                    type="password"
                    placeholder={calcomSettings?.hasApiKey ? "••••••••••••••••" : "cal_live_..."}
                    value={calcomApiKey}
                    onChange={(e) => setCalcomApiKey(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="calcom-username">Cal.com Username <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input
                    id="calcom-username"
                    placeholder="xenonlabs.ai"
                    value={calcomUsername}
                    onChange={(e) => setCalcomUsername(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank to use default: xenonlabs.ai
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="calcom-event-slug">Event Type Slug <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input
                    id="calcom-event-slug"
                    placeholder="xevoice-pro-ai-based-voice-calling-platform"
                    value={calcomEventSlug}
                    onChange={(e) => setCalcomEventSlug(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank to use default XeVoice event type
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="calcom-event-type-id">Event Type ID (Optional)</Label>
                  <Input
                    id="calcom-event-type-id"
                    type="number"
                    placeholder="123456"
                    value={calcomEventTypeId}
                    onChange={(e) => setCalcomEventTypeId(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional: The numeric ID of your event type. If not set, it will be auto-detected.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="calcom-guest-emails">Notification Emails (Optional)</Label>
                  <Input
                    id="calcom-guest-emails"
                    type="text"
                    placeholder="sales@company.com, team@company.com"
                    value={calcomGuestEmails}
                    onChange={(e) => setCalcomGuestEmails(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated email addresses that will be notified of all bookings (e.g., sales team members).
                  </p>
                </div>
              </div>

              {calcomTestResult && (
                <div className={`p-4 rounded-lg border ${calcomTestResult.success ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'}`}>
                  <div className="flex items-start gap-3">
                    {calcomTestResult.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                    )}
                    <div className="space-y-2">
                      <p className={`text-sm font-medium ${calcomTestResult.success ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                        {calcomTestResult.message}
                      </p>
                      {calcomTestResult.eventTypes && calcomTestResult.eventTypes.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Available event types:</p>
                          <ul className="text-xs space-y-1">
                            {calcomTestResult.eventTypes.map((et) => (
                              <li key={et.id} className="flex items-center gap-2">
                                <span className="font-mono bg-muted px-1 rounded">{et.slug}</span>
                                <span className="text-muted-foreground">- {et.title} ({et.duration} min)</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => testCalcomConnectionMutation.mutate()}
                  disabled={testCalcomConnectionMutation.isPending || !calcomSettings?.hasApiKey}
                  title={!calcomSettings?.hasApiKey ? "Save your API key first to test the connection" : undefined}
                >
                  {testCalcomConnectionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Test Connection
                </Button>
                <Button
                  onClick={() => saveCalcomSettingsMutation.mutate()}
                  disabled={saveCalcomSettingsMutation.isPending}
                >
                  {saveCalcomSettingsMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save Cal.com Settings
                </Button>
              </div>
            </div>
          )}

          {/* Microsoft Outlook Settings */}
          {calendarProvider === 'microsoft' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-4">
                <div className="flex items-start gap-3">
                  <CalendarDays className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Microsoft Outlook Integration
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Connect your Microsoft 365 account to check calendar availability and schedule meetings automatically during voice calls.
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                      Configuration is managed via environment variables. Contact your administrator to set up Azure AD credentials.
                    </p>
                  </div>
                </div>
              </div>

              {calendarProviderSettings?.microsoftConfigured ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="text-sm text-green-700 dark:text-green-300">
                    Microsoft Outlook integration is active
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <span className="text-sm text-amber-700 dark:text-amber-300">
                    Microsoft credentials not configured. Contact your administrator.
                  </span>
                </div>
              )}

              <div className="rounded-lg border p-4 space-y-3">
                <h4 className="text-sm font-medium">Required Environment Variables</h4>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li><code className="bg-muted px-1 rounded">AZURE_CLIENT_ID</code> - Azure AD Application ID</li>
                  <li><code className="bg-muted px-1 rounded">AZURE_CLIENT_SECRET</code> - Azure AD Client Secret</li>
                  <li><code className="bg-muted px-1 rounded">AZURE_TENANT_ID</code> - Azure AD Tenant ID</li>
                  <li><code className="bg-muted px-1 rounded">AZURE_USER_ID</code> - User email or Object ID for calendar access</li>
                </ul>
              </div>

              {microsoftTestResult && (
                <div className={`p-4 rounded-lg border ${microsoftTestResult.success ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'}`}>
                  <div className="flex items-start gap-3">
                    {microsoftTestResult.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                    )}
                    <p className={`text-sm font-medium ${microsoftTestResult.success ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                      {microsoftTestResult.message}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => testMicrosoftConnectionMutation.mutate()}
                  disabled={testMicrosoftConnectionMutation.isPending}
                >
                  {testMicrosoftConnectionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Test Microsoft Connection
                </Button>
              </div>
            </div>
          )}

          {/* Google Calendar Settings */}
          {calendarProvider === 'google' && (
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-4">
                <div className="flex items-start gap-3">
                  <CalendarDays className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      Google Calendar Integration
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Connect your Google account to check calendar availability and create Google Meet links automatically during voice calls.
                    </p>
                  </div>
                </div>
              </div>

              {googleCalendarStatus?.connected ? (
                <>
                  <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-green-900 dark:text-green-100">
                            Google Calendar Connected
                          </p>
                          {googleCalendarStatus.email && (
                            <p className="text-sm text-green-700 dark:text-green-300">
                              {googleCalendarStatus.email}
                            </p>
                          )}
                          {googleCalendarStatus.isShared && (
                            <p className="text-xs text-green-600 dark:text-green-400">
                              Using shared calendar configuration
                            </p>
                          )}
                        </div>
                      </div>
                      {!googleCalendarStatus.isShared && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => disconnectGoogleCalendarMutation.mutate()}
                          disabled={disconnectGoogleCalendarMutation.isPending}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          {disconnectGoogleCalendarMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Disconnect
                        </Button>
                      )}
                    </div>
                  </div>

                  {googleCalendarStatus.lastError && (
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          Last error: {googleCalendarStatus.lastError}
                        </p>
                      </div>
                    </div>
                  )}

                  {googleTestResult && (
                    <div className={`p-4 rounded-lg border ${googleTestResult.success ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'}`}>
                      <div className="flex items-start gap-3">
                        {googleTestResult.success ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                        )}
                        <p className={`text-sm font-medium ${googleTestResult.success ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
                          {googleTestResult.message}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => testGoogleConnectionMutation.mutate()}
                      disabled={testGoogleConnectionMutation.isPending}
                    >
                      {testGoogleConnectionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Test Connection
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-4 rounded-lg border border-dashed border-muted-foreground/25 bg-muted/20">
                    <div className="flex flex-col items-center justify-center py-6 space-y-4">
                      <div className="p-3 rounded-full bg-muted">
                        <CalendarDays className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <div className="text-center space-y-1">
                        <p className="text-sm font-medium">Connect Your Google Calendar</p>
                        <p className="text-xs text-muted-foreground">
                          Sign in with your Google account to enable calendar availability checking and automatic Google Meet links.
                        </p>
                      </div>
                      <Button
                        onClick={() => connectGoogleCalendarMutation.mutate()}
                        disabled={connectGoogleCalendarMutation.isPending}
                        className="mt-2"
                      >
                        {connectGoogleCalendarMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        Connect with Google
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-lg border p-4 space-y-2">
                    <h4 className="text-sm font-medium">What you get:</h4>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        Real-time calendar availability checking
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        Automatic Google Meet link generation
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        Calendar events created on your behalf
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        Meeting invites sent to attendees
                      </li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
